/**
 * The company's UPS shipper accounts — the account a label is created under (UPS API auth,
 * the negotiated-rate source, and the account billed on bill-to-shipper). One per department.
 *
 * The account NUMBERS are NOT stored here (kept out of source). They live in the `api` Appwrite
 * function's environment variables (see PLASTICS_/METALS_UPS_ACCOUNT_NUMBER) and are merged in
 * server-side by getUpsShipperAccounts(), then handed to the client via the ups-get-accounts
 * endpoint. The from-name/address are NOT stored here either — they come from the ship-from
 * selection (a customer/address in Appwrite), defaulted per account by defaultShipFrom.
 */
/**
 * Default ship-from selection for an account — pointers to existing documents (a Customer, one of
 * its saved CustomerAddresses, and an associated Contact). When the account is chosen on a fresh
 * shipment the builder preselects these in the ship-from "existing" tab (overridable). The address
 * / contact print as the label's from-address + from-contact. All three are ids into the same
 * Customer, so the contact is a department-specific person while the customer + address are shared.
 */
export interface UpsShipperDefaultShipFrom {
    /** Customer document id (the ship-from company). */
    customerId: string;
    /** Id of one of that customer's saved addresses. */
    addressId: string;
    /** Id of a Contact associated with that customer ('' for none). */
    contactId: string;
}

export interface UpsShipperAccountTemplate {
    /** Department key — also drives which env var supplies the number. */
    key: 'plastics' | 'metals';
    /** UI label for the shipper-account select. */
    label: string;
    /** Function env var holding this account's UPS Shipper Number. */
    envKey: string;
    /** Optional default ship-from selection prefilled when this account is chosen. Omit for none. */
    defaultShipFrom?: UpsShipperDefaultShipFrom;
}

/** A resolved shipper account (template + the number injected from env). What the endpoint returns
 *  and the client builds the request from. `defaultShipFrom` is intentionally NOT here — it's
 *  resolved on the client from getUpsShipperAccountTemplates() so the ids match the client's Appwrite
 *  project. The account's from-name/address come from the ship-from selection (DB), not the account. */
export interface UpsShipperAccount {
    key: string;
    label: string;
    shipperNumber: string;
}


/**
 * The shipper-account templates. `isProduction` selects which Appwrite project's document ids to use
 * for each default ship-from field — the testing and production projects hold the same logical
 * records under different ids. Pass the same prod check used to pick the Appwrite project
 * (Production / LocalProduction). Fill the ternaries below with the real ids (production : testing);
 * leave a field '' to skip it, or omit defaultShipFrom entirely for no default.
 */
export function getUpsShipperAccountTemplates(isProduction: boolean): UpsShipperAccountTemplate[] {
    return [
        {
            key: 'plastics', label: 'Plastics', envKey: 'PLASTICS_UPS_ACCOUNT_NUMBER',
            defaultShipFrom: {
                customerId: isProduction ? 'PtxI9Qu4TQDerKVh68Ai' : 'PtxI9Qu4TQDerKVh68Ai',
                addressId: isProduction ? 'gPRXJ3mAK2exnz2oCzhP' : 'gPRXJ3mAK2exnz2oCzhP',
                contactId: isProduction ? 'lpqKQloQfh72wkiNGkJX' : 'okdkwWEW7DHHJPK8Jqf1',
            },
        },
        {
            key: 'metals', label: 'Metals', envKey: 'METALS_UPS_ACCOUNT_NUMBER',
            defaultShipFrom: {
                customerId: isProduction ? 'PtxI9Qu4TQDerKVh68Ai' : 'PtxI9Qu4TQDerKVh68Ai',
                addressId: isProduction ? 'gPRXJ3mAK2exnz2oCzhP' : 'gPRXJ3mAK2exnz2oCzhP',
                contactId: isProduction ? 'XXLXocYrQ5gVaJCmROj8' : 'Ug7z4ZLgSLFYljJpKCtT',
            },
        },
    ];
}
